import { useEffect, useRef, useState } from "react";
import { api, API_BASE, euro, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Upload, Building2, Headphones, Download, Database, AlertTriangle, Plug, RefreshCw, CheckCircle2, FileCheck2, Send, ShieldCheck, Percent, Printer } from "lucide-react";
import { isDesktop, listPrinters, getSavedPrinter, savePrinter } from "@/lib/ticketPrinter";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function Settings() {
  const { user, centers } = useAuth();
  const [settings, setSettings] = useState(null);
  const [centersList, setCentersList] = useState([]);
  const [suppliersList, setSuppliersList] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [s, c, sup] = await Promise.all([
      api.get("/settings").then((r) => r.data),
      api.get("/centers").then((r) => r.data),
      api.get("/suppliers").then((r) => r.data).catch(() => []),
    ]);
    setSettings(s);
    setCentersList(c);
    setSuppliersList(sup);
  };

  useEffect(() => {
    load();
  }, []);

  if (!settings) return <div className="text-[#6E7772]">Cargando…</div>;

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings", {
        company_name: settings.company_name,
        company_cif: settings.company_cif,
        company_address: settings.company_address,
        company_phone: settings.company_phone,
        company_email: settings.company_email,
        default_iva_rate: parseFloat(settings.default_iva_rate) || 21,
        quote_validity_days: parseInt(settings.quote_validity_days) || 30,
        renove_years: parseFloat(settings.renove_years) || 5,
        hearing_aid_catalog: settings.hearing_aid_catalog || [],
      });
      setSettings(data);
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
          Administración
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
          Configuración
        </h1>
        <p className="text-sm text-[#6E7772] mt-1">
          Datos de la empresa, centros y catálogo de audífonos.
        </p>
      </div>

      <Tabs defaultValue="centros">
        <TabsList className="bg-[#EAE6DF] flex-wrap h-auto">
          <TabsTrigger value="centros" data-testid="tab-centros"><Building2 className="h-4 w-4 mr-1.5" /> Centros y datos fiscales</TabsTrigger>
          <TabsTrigger value="ajustes" data-testid="tab-ajustes">Ajustes generales</TabsTrigger>
          <TabsTrigger value="audifonos" data-testid="tab-audifonos"><Headphones className="h-4 w-4 mr-1.5" /> Catálogo productos</TabsTrigger>
          <TabsTrigger value="iva" data-testid="tab-iva"><Percent className="h-4 w-4 mr-1.5" /> IVA por grupos</TabsTrigger>
          <TabsTrigger value="plantillas" data-testid="tab-plantillas"><FileCheck2 className="h-4 w-4 mr-1.5" /> Plantillas</TabsTrigger>
          <TabsTrigger value="verifactu" data-testid="tab-verifactu"><FileCheck2 className="h-4 w-4 mr-1.5" /> Veri*Factu</TabsTrigger>
          <TabsTrigger value="noah" data-testid="tab-noah"><Plug className="h-4 w-4 mr-1.5" /> Noah</TabsTrigger>
          <TabsTrigger value="data" data-testid="tab-data">Importar / Exportar</TabsTrigger>
        </TabsList>

        <TabsContent value="ajustes" className="mt-6">
          <Card className="border-[#E5E3DB] shadow-none bg-white">
            <CardHeader>
              <CardTitle className="font-heading font-medium text-lg">Ajustes generales</CardTitle>
              <p className="text-xs text-[#6E7772] mt-1">
                Los datos fiscales (razón social, CIF, dirección) ahora están en cada centro: ve a la pestaña «Centros y datos fiscales».
              </p>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>IVA por defecto (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings.default_iva_rate || ""}
                  onChange={(e) => setSettings({ ...settings, default_iva_rate: e.target.value })}
                />
              </div>
              <div>
                <Label>Validez de presupuestos (días)</Label>
                <Input
                  type="number"
                  value={settings.quote_validity_days || ""}
                  onChange={(e) => setSettings({ ...settings, quote_validity_days: e.target.value })}
                />
              </div>
              <div>
                <Label>Plan Renove · umbral (años)</Label>
                <Input
                  data-testid="renove-years"
                  type="number"
                  min={1}
                  step="0.5"
                  value={settings.renove_years || ""}
                  onChange={(e) => setSettings({ ...settings, renove_years: e.target.value })}
                  placeholder="5"
                />
                <p className="text-xs text-[#6E7772] mt-1">
                  Tras estos años desde la adaptación, recibirás un aviso para ofrecer la renovación al paciente.
                </p>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button
                  onClick={saveSettings}
                  disabled={saving}
                  data-testid="save-settings-btn"
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  {saving ? "Guardando…" : "Guardar cambios"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="centros" className="mt-6">
          <CentersTab centers={centersList} onChange={load} />
        </TabsContent>

        <TabsContent value="plantillas" className="mt-6">
          <TemplatesTab centers={centersList} />
        </TabsContent>

        <TabsContent value="audifonos" className="mt-6">
          <CatalogTab
            catalog={settings.hearing_aid_catalog || []}
            suppliers={suppliersList}
            ivaCategories={settings.iva_categories || []}
            onChange={async (newCatalog) => {
              try {
                const { data } = await api.patch("/settings", {
                  hearing_aid_catalog: newCatalog,
                });
                setSettings(data);
                toast.success("Catálogo actualizado");
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }}
          />
        </TabsContent>

        <TabsContent value="iva" className="mt-6">
          <IvaCategoriesTab
            categories={settings.iva_categories || []}
            onChange={async (newCats) => {
              try {
                const { data } = await api.patch("/settings", {
                  iva_categories: newCats,
                });
                setSettings(data);
                toast.success("Tipos de IVA actualizados");
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }}
          />
        </TabsContent>

        <TabsContent value="verifactu" className="mt-6">
          <VerifactuTab />
        </TabsContent>

        <TabsContent value="noah" className="mt-6">
          <NoahTab />
        </TabsContent>

        <TabsContent value="data" className="mt-6">
          <DataTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CentersTab({ centers, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyCenterForm = {
    name: "", address: "", phone: "", email: "",
    business_type: "sl",
    fiscal_name: "", cif: "", fiscal_address: "",
    admin_name: "", owner_name: "", iae_epigrafe: "",
    brand_color: "#4A5D4E",
    printer_ticket: "", printer_a4: "", printer_default: "a4",
  };
  const [form, setForm] = useState(emptyCenterForm);

  const openNew = () => {
    setEditing(null);
    setForm(emptyCenterForm);
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      address: c.address || "",
      phone: c.phone || "",
      email: c.email || "",
      business_type: c.business_type || "sl",
      fiscal_name: c.fiscal_name || "",
      cif: c.cif || "",
      fiscal_address: c.fiscal_address || "",
      admin_name: c.admin_name || "",
      owner_name: c.owner_name || "",
      iae_epigrafe: c.iae_epigrafe || "",
      brand_color: c.brand_color || "#4A5D4E",
      printer_ticket: c.printer_ticket || "",
      printer_a4: c.printer_a4 || "",
      printer_default: c.printer_default || "a4",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/centers/${editing.id}`, form);
        toast.success("Centro actualizado");
      } else {
        await api.post("/centers", form);
        toast.success("Centro creado");
      }
      setOpen(false);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar centro?")) return;
    try {
      await api.delete(`/centers/${id}`);
      toast.success("Eliminado");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadCenterLogo = async (cid, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/centers/${cid}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo del centro actualizado");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteCenterLogo = async (cid) => {
    if (!window.confirm("¿Eliminar el logo del centro?")) return;
    try {
      await api.delete(`/centers/${cid}/logo`);
      toast.success("Logo eliminado");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading font-medium text-lg">Centros y datos fiscales</CardTitle>
          <p className="text-xs text-[#6E7772] mt-1">Cada centro tiene su propia razón social, CIF, dirección fiscal y logo. Se usan al emitir facturas y presupuestos.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNew}
              data-testid="new-center-btn"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Plus className="h-4 w-4 mr-2" /> Nuevo centro
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {editing ? "Editar centro" : "Nuevo centro"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Nombre comercial del centro *</Label>
                <Input
                  data-testid="center-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej. GestiónAudio Torrellano"
                />
              </div>

              <div className="md:col-span-2 mt-2 pt-2 border-t border-[#E5E3DB]">
                <div className="text-xs uppercase tracking-[0.2em] text-[#6E7772] font-semibold mb-2">
                  Tipo de empresa
                </div>
                <div className="flex gap-2" data-testid="center-business-type">
                  {[
                    { value: "sl", label: "Sociedad Limitada (SL)" },
                    { value: "autonomo", label: "Autónomo" },
                    { value: "otro", label: "Otro" },
                  ].map((opt) => (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setForm({ ...form, business_type: opt.value })}
                      data-testid={`btype-${opt.value}`}
                      className={`flex-1 px-3 py-2 rounded-md text-sm border transition-colors ${
                        form.business_type === opt.value
                          ? "bg-[#4A5D4E] text-white border-[#4A5D4E]"
                          : "bg-white text-[#4B5563] border-[#E5E3DB] hover:bg-[#FAF9F6]"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {form.business_type === "sl" && (
                <>
                  <div>
                    <Label>Razón social *</Label>
                    <Input
                      data-testid="center-fiscal-name"
                      value={form.fiscal_name}
                      onChange={(e) => setForm({ ...form, fiscal_name: e.target.value })}
                      placeholder="Ej. Audífonos García SL"
                    />
                  </div>
                  <div>
                    <Label>CIF *</Label>
                    <Input
                      data-testid="center-cif"
                      value={form.cif}
                      onChange={(e) => setForm({ ...form, cif: e.target.value })}
                      placeholder="Ej. B12345678"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Administrador (opcional)</Label>
                    <Input
                      data-testid="center-admin-name"
                      value={form.admin_name}
                      onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                      placeholder="Nombre del administrador único / consejero"
                    />
                  </div>
                </>
              )}

              {form.business_type === "autonomo" && (
                <>
                  <div className="md:col-span-2">
                    <Label>Nombre y apellidos *</Label>
                    <Input
                      data-testid="center-owner-name"
                      value={form.owner_name}
                      onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                      placeholder="Ej. María García López"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>NIF *</Label>
                    <Input
                      data-testid="center-cif"
                      value={form.cif}
                      onChange={(e) => setForm({ ...form, cif: e.target.value })}
                      placeholder="Ej. 12345678A"
                    />
                  </div>
                </>
              )}

              {form.business_type === "otro" && (
                <>
                  <div>
                    <Label>Denominación</Label>
                    <Input
                      value={form.fiscal_name}
                      onChange={(e) => setForm({ ...form, fiscal_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>NIF / CIF</Label>
                    <Input
                      value={form.cif}
                      onChange={(e) => setForm({ ...form, cif: e.target.value })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Dirección fiscal</Label>
                    <Input
                      value={form.fiscal_address}
                      onChange={(e) => setForm({ ...form, fiscal_address: e.target.value })}
                    />
                  </div>
                </>
              )}

              <div className="md:col-span-2 mt-2 pt-2 border-t border-[#E5E3DB]">
                <div className="text-xs uppercase tracking-[0.2em] text-[#6E7772] font-semibold mb-2">Datos de contacto</div>
              </div>
              <div className="md:col-span-2">
                <Label>Dirección física</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>

              <div className="md:col-span-2 mt-2 pt-2 border-t border-[#E5E3DB]">
                <div className="text-xs uppercase tracking-[0.2em] text-[#6E7772] font-semibold mb-2">Imagen de marca</div>
                <p className="text-xs text-[#6E7772] mb-3">
                  Color corporativo que se aplicará a la cabecera, totales y línea de acento en facturas y presupuestos de este centro.
                </p>
              </div>
              <div className="md:col-span-2 flex items-center gap-3">
                <Label className="mb-0">Color corporativo</Label>
                <input
                  type="color"
                  data-testid="center-brand-color"
                  value={form.brand_color || "#4A5D4E"}
                  onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                  className="h-10 w-16 rounded border border-[#E5E3DB] cursor-pointer p-1 bg-white"
                />
                <Input
                  value={form.brand_color || "#4A5D4E"}
                  onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                  className="w-32 font-mono text-sm"
                  placeholder="#4A5D4E"
                />
                <div
                  className="h-10 flex-1 rounded border border-[#E5E3DB]"
                  style={{ backgroundColor: form.brand_color || "#4A5D4E" }}
                  title="Vista previa"
                />
              </div>

              <div className="md:col-span-2 mt-2 pt-2 border-t border-[#E5E3DB]">
                <div className="text-xs uppercase tracking-[0.2em] text-[#6E7772] font-semibold mb-2">Impresoras vinculadas</div>
                <p className="text-xs text-[#6E7772] mb-3">
                  Indica el nombre exacto de tus impresoras (como aparecen en el SO). El navegador
                  abrirá su diálogo de impresión apuntando a la impresora correcta.
                </p>
              </div>
              <div>
                <Label>Impresora térmica (ticket 80mm)</Label>
                <Input
                  data-testid="center-printer-ticket"
                  value={form.printer_ticket}
                  onChange={(e) => setForm({ ...form, printer_ticket: e.target.value })}
                  placeholder="Ej. EPSON TM-T20III"
                />
              </div>
              <div>
                <Label>Impresora normal (A4)</Label>
                <Input
                  data-testid="center-printer-a4"
                  value={form.printer_a4}
                  onChange={(e) => setForm({ ...form, printer_a4: e.target.value })}
                  placeholder="Ej. Brother HL-L2360DW"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Formato por defecto</Label>
                <div className="flex gap-2 mt-1">
                  {[
                    { value: "a4", label: "A4 (factura completa)" },
                    { value: "ticket", label: "Ticket (80mm)" },
                  ].map((opt) => (
                    <button
                      type="button"
                      key={opt.value}
                      data-testid={`printer-default-${opt.value}`}
                      onClick={() => setForm({ ...form, printer_default: opt.value })}
                      className={`flex-1 px-3 py-2 rounded-md text-sm border transition-colors ${
                        form.printer_default === opt.value
                          ? "bg-[#4A5D4E] text-white border-[#4A5D4E]"
                          : "bg-white text-[#4B5563] border-[#E5E3DB] hover:bg-[#FAF9F6]"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <DialogFooter className="md:col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="save-center-btn"
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5E3DB]">
              <TableHead>Centro</TableHead>
              <TableHead>Razón social · CIF</TableHead>
              <TableHead>Contacto</TableHead>
              <TableHead>Logo</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {centers.map((c) => (
              <TableRow key={c.id} className="border-[#E5E3DB]">
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-3 w-3 rounded-full border border-[#E5E3DB]"
                      style={{ backgroundColor: c.brand_color || "#4A5D4E" }}
                      title={`Color: ${c.brand_color || "#4A5D4E"}`}
                      data-testid={`center-color-chip-${c.id}`}
                    />
                    {c.name}
                  </div>
                  <div className="text-xs text-[#6E7772]">{c.fiscal_address || c.address || "—"}</div>
                </TableCell>
                <TableCell className="text-sm">
                  <div className="flex items-center gap-2">
                    {c.fiscal_name || <span className="text-[#9CA3AF]">Sin razón social</span>}
                    {c.business_type === "autonomo" && <Badge className="bg-[#EAE6DF] text-[#2A2F2C] text-[10px]">Autónomo</Badge>}
                    {c.business_type === "sl" && <Badge className="bg-[#D1E0D7] text-[#2A2F2C] text-[10px]">SL</Badge>}
                  </div>
                  <div className="text-xs text-[#6E7772]">{c.cif || "—"}</div>
                </TableCell>
                <TableCell className="text-sm">
                  <div>{c.phone || "—"}</div>
                  <div className="text-xs text-[#6E7772]">{c.email || "—"}</div>
                </TableCell>
                <TableCell>
                  {c.logo_path ? (
                    <div className="flex items-center gap-2">
                      <img
                        src={`${API_BASE}/centers/${c.id}/logo?t=${Date.now()}`}
                        alt="logo"
                        className="h-10 w-10 object-contain border rounded"
                      />
                      <Button size="sm" variant="ghost" onClick={() => deleteCenterLogo(c.id)} data-testid={`del-center-logo-${c.id}`}>
                        <Trash2 className="h-3 w-3 text-[#C07F67]" />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-[#9CA3AF]">Sin logo</span>
                  )}
                </TableCell>
                <TableCell className="space-x-1 text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(c)}
                    data-testid={`edit-center-${c.id}`}
                  >
                    Editar
                  </Button>
                  <label className="inline-block">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadCenterLogo(c.id, f);
                        e.target.value = "";
                      }}
                      data-testid={`upload-center-logo-${c.id}`}
                    />
                    <Button asChild variant="outline" size="sm">
                      <span className="cursor-pointer"><Upload className="h-3 w-3 mr-1" />Logo</span>
                    </Button>
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(c.id)}
                  >
                    <Trash2 className="h-4 w-4 text-[#C07F67]" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// --------------------------------------------------------------------------- #
// Plantillas personalizables — placeholder (próxima iteración: upload .docx/.pdf)
// --------------------------------------------------------------------------- #
function TemplatesTab({ centers }) {
  const [list, setList] = useState([]);
  const [centerFilter, setCenterFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [systemFields, setSystemFields] = useState([]);
  const [editing, setEditing] = useState(null); // template being edited
  // Form de subida
  const [upCenter, setUpCenter] = useState("");
  const [upKind, setUpKind] = useState("invoice");
  const [upFile, setUpFile] = useState(null);
  const [upNotes, setUpNotes] = useState("");

  const load = async () => {
    const params = {};
    if (centerFilter !== "all") params.center_id = centerFilter;
    if (kindFilter !== "all") params.kind = kindFilter;
    const { data } = await api.get("/templates", { params });
    setList(data || []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [centerFilter, kindFilter]);

  useEffect(() => {
    api.get("/templates/system-fields").then((r) => setSystemFields(r.data?.fields || [])).catch(() => {});
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!upCenter || !upKind || !upFile) {
      toast.error("Selecciona centro, tipo y archivo");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("center_id", upCenter);
      fd.append("kind", upKind);
      fd.append("file", upFile);
      fd.append("notes", upNotes || "");
      const { data } = await api.post("/templates", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Plantilla subida (${data.fmt}). Placeholders detectados: ${(data.placeholders || []).length}`,
      );
      setUpFile(null);
      setUpNotes("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const toggleActive = async (tpl, active) => {
    try {
      await api.patch(`/templates/${tpl.id}`, { active });
      toast.success(active ? "Plantilla activada" : "Plantilla desactivada");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (tpl) => {
    if (!window.confirm(`¿Eliminar plantilla "${tpl.filename}"?`)) return;
    try {
      await api.delete(`/templates/${tpl.id}`);
      toast.success("Plantilla eliminada");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const centerName = (cid) => centers.find((c) => c.id === cid)?.name || "—";
  const kindLabel = (k) => ({ invoice: "Factura", quote: "Presupuesto" })[k] || k;
  const fmtBadge = (f) => {
    const m = {
      docx: { label: "Word", className: "bg-[#D7E0EA] text-[#1F2A24]" },
      pdf_form: { label: "PDF (formulario)", className: "bg-[#E0D7C8] text-[#3F2E14]" },
      pdf_overlay: { label: "PDF (decorativa)", className: "bg-[#EAE6DF] text-[#1F2A24]" },
    };
    return m[f] || { label: f, className: "bg-[#EAE6DF]" };
  };

  return (
    <div className="space-y-6" data-testid="templates-tab">
      <Card className="border-[#E5E3DB] bg-white">
        <CardHeader>
          <CardTitle className="font-heading text-2xl">Subir nueva plantilla</CardTitle>
          <p className="text-xs text-[#6E7772] mt-1">
            Sube un archivo <strong>.docx</strong> (con variables{" "}
            <code className="bg-[#FAF9F6] px-1 rounded text-[11px]">{"{{patient_full_name}}"}</code>{" "}
            <code className="bg-[#FAF9F6] px-1 rounded text-[11px]">{"{{total}}"}</code> …) o un{" "}
            <strong>.pdf</strong> (con campos AcroForm o como cabecera decorativa).
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={upload} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label>Centro</Label>
              <Select value={upCenter} onValueChange={setUpCenter}>
                <SelectTrigger data-testid="template-upload-center"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent>
                  {centers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={upKind} onValueChange={setUpKind}>
                <SelectTrigger data-testid="template-upload-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice">Factura</SelectItem>
                  <SelectItem value="quote">Presupuesto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Archivo (.docx o .pdf)</Label>
              <Input
                type="file"
                accept=".docx,.pdf"
                data-testid="template-upload-file"
                onChange={(e) => setUpFile(e.target.files?.[0] || null)}
              />
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Input value={upNotes} onChange={(e) => setUpNotes(e.target.value)} />
            </div>
            <div className="md:col-span-4">
              <Button
                type="submit"
                disabled={uploading}
                data-testid="template-upload-btn"
                className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
              >
                {uploading ? "Subiendo…" : "Subir plantilla"}
              </Button>
            </div>
          </form>

          {systemFields.length > 0 && (
            <details className="mt-4 text-xs text-[#3F4744]">
              <summary className="cursor-pointer font-semibold uppercase tracking-wider text-[#6E7772]">
                Variables disponibles ({systemFields.length})
              </summary>
              <div className="mt-2 flex flex-wrap gap-1">
                {systemFields.map((f) => (
                  <code
                    key={f}
                    className="bg-[#FAF9F6] border border-[#E5E3DB] px-2 py-0.5 rounded text-[11px]"
                  >
                    {"{{" + f + "}}"}
                  </code>
                ))}
              </div>
              <p className="text-[11px] text-[#6E7772] mt-2">
                <code className="bg-[#FAF9F6] px-1 rounded">{"{{items_table}}"}</code> se sustituye
                por la tabla completa de líneas de la factura/presupuesto. Para PDFs con campos
                AcroForm el nombre del campo debe coincidir con la variable (ej. campo
                "patient_full_name").
              </p>
            </details>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB] bg-white">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="font-heading text-2xl">Plantillas existentes</CardTitle>
          <div className="flex gap-2">
            <Select value={centerFilter} onValueChange={setCenterFilter}>
              <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los centros</SelectItem>
                {centers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="invoice">Facturas</SelectItem>
                <SelectItem value="quote">Presupuestos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {list.length === 0 ? (
            <div className="text-center text-[#6E7772] py-8 text-sm">
              No hay plantillas todavía. Sube una arriba.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Centro</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Formato</TableHead>
                  <TableHead>Archivo</TableHead>
                  <TableHead>Placeholders</TableHead>
                  <TableHead>Activa</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((tpl) => {
                  const fb = fmtBadge(tpl.fmt);
                  return (
                    <TableRow key={tpl.id} data-testid={`template-row-${tpl.id}`}>
                      <TableCell className="text-sm">{centerName(tpl.center_id)}</TableCell>
                      <TableCell>{kindLabel(tpl.kind)}</TableCell>
                      <TableCell><Badge className={fb.className}>{fb.label}</Badge></TableCell>
                      <TableCell className="text-xs font-mono max-w-[220px] truncate" title={tpl.filename}>
                        {tpl.filename}
                      </TableCell>
                      <TableCell className="text-xs">
                        {(tpl.placeholders || []).length === 0 ? (
                          <span className="text-[#9CA3AF]">—</span>
                        ) : (
                          <span className="text-[#6E7772]">
                            {(tpl.placeholders || []).length} detectados
                            {tpl.field_map && Object.keys(tpl.field_map).length > 0 && (
                              <span className="ml-1 text-[#4A5D4E]">
                                · {Object.keys(tpl.field_map).length} mapeados
                              </span>
                            )}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={!!tpl.active}
                          onChange={(e) => toggleActive(tpl, e.target.checked)}
                          className="h-4 w-4 accent-[#4A5D4E]"
                          data-testid={`template-active-${tpl.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          {(tpl.placeholders || []).length > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditing(tpl)}
                              data-testid={`template-edit-${tpl.id}`}
                              className="h-7 text-xs"
                            >
                              Mapear
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => remove(tpl)}
                            data-testid={`template-del-${tpl.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-[#C07F67]" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {editing && (
        <TemplateFieldMapDialog
          template={editing}
          systemFields={systemFields}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TemplateFieldMapDialog({ template, systemFields, onClose, onSaved }) {
  const [fieldMap, setFieldMap] = useState({ ...(template.field_map || {}) });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/templates/${template.id}`, { field_map: fieldMap });
      toast.success("Mapeo guardado");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Mapear placeholders de la plantilla</DialogTitle>
          <p className="text-xs text-[#6E7772] mt-1">
            Asocia cada marcador detectado en tu plantilla a un campo del sistema. Los que no
            mapees quedarán vacíos al generar el documento.
          </p>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Placeholder en plantilla</TableHead>
                <TableHead>Campo del sistema</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(template.placeholders || []).map((ph) => (
                <TableRow key={ph}>
                  <TableCell>
                    <code className="bg-[#FAF9F6] border border-[#E5E3DB] px-2 py-0.5 rounded text-xs">
                      {ph}
                    </code>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={fieldMap[ph] || ""}
                      onValueChange={(v) => setFieldMap((m) => ({ ...m, [ph]: v === "__none__" ? "" : v }))}
                    >
                      <SelectTrigger data-testid={`tpl-map-${ph}`}>
                        <SelectValue placeholder="— Sin mapear —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Sin mapear —</SelectItem>
                        {systemFields.map((f) => (
                          <SelectItem key={f} value={f}>{f}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            data-testid="tpl-map-save"
          >
            {saving ? "Guardando…" : "Guardar mapeo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IvaCategoriesTab({ categories, onChange }) {
  const [items, setItems] = useState(categories);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems(categories);
  }, [categories]);

  const updateItem = (idx, field, value) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], [field]: value };
    setItems(copy);
  };

  const addItem = () => {
    setItems([...items, { key: "", label: "", iva_rate: 21 }]);
  };

  const removeItem = (idx) => {
    if (!window.confirm("¿Eliminar esta categoría de IVA?")) return;
    setItems(items.filter((_, i) => i !== idx));
  };

  const save = async () => {
    // Validación cliente: key + label requeridos, sin duplicados
    const cleaned = items
      .map((it) => ({
        key: (it.key || "").trim().toLowerCase().replace(/\s+/g, "_"),
        label: (it.label || "").trim(),
        iva_rate: parseFloat(it.iva_rate) || 0,
      }))
      .filter((it) => it.key && it.label);
    const keys = cleaned.map((c) => c.key);
    if (new Set(keys).size !== keys.length) {
      toast.error("Hay claves duplicadas. Revísalas antes de guardar.");
      return;
    }
    setSaving(true);
    try {
      await onChange(cleaned);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="iva-tab">
      <CardHeader>
        <CardTitle className="font-heading font-medium text-lg">IVA por grupos de producto</CardTitle>
        <p className="text-xs text-[#6E7772] mt-1">
          Define los grupos de productos y su % de IVA. Solo el <strong>administrador</strong> puede modificarlos.
          Cuando un trabajador añade una línea a una factura, el IVA se aplica automáticamente según el grupo
          (audífonos, accesorios, pilas, etc.) y <strong>no es editable</strong>.
          Si el paciente tiene <strong>discapacidad</strong>, todas las líneas usan 4 % independientemente del grupo.
        </p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5E3DB]">
              <TableHead className="w-44">Clave técnica</TableHead>
              <TableHead>Nombre visible</TableHead>
              <TableHead className="w-32 text-right">IVA (%)</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-[#6E7772] py-8">
                  No hay categorías. Añade la primera.
                </TableCell>
              </TableRow>
            )}
            {items.map((it, idx) => (
              <TableRow key={idx} className="border-[#E5E3DB]" data-testid={`iva-row-${idx}`}>
                <TableCell>
                  <Input
                    value={it.key || ""}
                    onChange={(e) => updateItem(idx, "key", e.target.value)}
                    placeholder="ej. pilas"
                    className="font-mono text-sm"
                    data-testid={`iva-key-${idx}`}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={it.label || ""}
                    onChange={(e) => updateItem(idx, "label", e.target.value)}
                    placeholder="ej. Pilas"
                    data-testid={`iva-label-${idx}`}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    value={it.iva_rate ?? ""}
                    onChange={(e) => updateItem(idx, "iva_rate", e.target.value)}
                    className="text-right"
                    data-testid={`iva-rate-${idx}`}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-[#C07F67]"
                    onClick={() => removeItem(idx)}
                    data-testid={`iva-remove-${idx}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="mt-4 flex justify-between items-center">
          <Button
            variant="outline"
            onClick={addItem}
            data-testid="iva-add-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Añadir grupo
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="iva-save-btn"
            className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
          >
            {saving ? "Guardando…" : "Guardar cambios"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CatalogTab({ catalog, onChange, suppliers = [], ivaCategories = [] }) {
  const [items, setItems] = useState(catalog);
  const [form, setForm] = useState({ brand: "", model: "", price: 0, notes: "", type: "audifono", warranty_years: 0, default_supplier_id: "", iva_category: "", pila_size: "" });
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    setItems(catalog);
  }, [catalog]);

  const submit = async (e) => {
    e.preventDefault();
    const entry = {
      brand: form.brand,
      model: form.model,
      price: parseFloat(form.price) || 0,
      notes: form.notes,
      type: form.type || "audifono",
      warranty_years: parseFloat(form.warranty_years) || 0,
      default_supplier_id: form.default_supplier_id || null,
      iva_category: form.iva_category || null,
      pila_size: form.type === "pila" ? (form.pila_size || null) : null,
    };
    const newList = editing != null
      ? items.map((it, i) => (i === editing ? entry : it))
      : [...items, entry];
    await onChange(newList);
    setOpen(false);
    setForm({ brand: "", model: "", price: 0, notes: "", type: "audifono", warranty_years: 0, default_supplier_id: "", iva_category: "", pila_size: "" });
    setEditing(null);
  };

  const remove = async (idx) => {
    if (!window.confirm("¿Eliminar de catálogo?")) return;
    const realIdx = filteredIndexes[idx];
    const newList = items.filter((_, i) => i !== realIdx);
    await onChange(newList);
  };

  const CATALOG_TYPE_OPTIONS = [
    { value: "audifono", label: "Audífono" },
    { value: "accesorio", label: "Accesorio auditivo" },
    { value: "pila", label: "Pila" },
    { value: "tapon_bano_medida", label: "Tapón baño · a medida" },
    { value: "tapon_bano_standard", label: "Tapón baño · standard" },
    { value: "tapon_ruido_medida", label: "Tapón ruido · a medida" },
    { value: "tapon_ruido_standard", label: "Tapón ruido · standard" },
    { value: "otro", label: "Otro" },
  ];
  const TYPE_LABEL_MAP = Object.fromEntries(CATALOG_TYPE_OPTIONS.map((o) => [o.value, o.label]));
  const filteredItems = typeFilter === "all" ? items : items.filter((it) => (it.type || "audifono") === typeFilter);
  const filteredIndexes = items.reduce((acc, it, i) => {
    if (typeFilter === "all" || (it.type || "audifono") === typeFilter) acc.push(i);
    return acc;
  }, []);
  const supplierName = (id) => suppliers.find((s) => s.id === id)?.name || "—";

  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="font-heading font-medium text-lg">
          Catálogo de productos
        </CardTitle>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-48 bg-white" data-testid="catalog-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {CATALOG_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="inline-block">
            <input
              type="file"
              accept=".csv,.xlsx,.xls,.pdf"
              className="hidden"
              data-testid="import-catalog-input"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const replace = window.confirm("¿Reemplazar el catálogo actual?\n\n• Aceptar → reemplaza todo\n• Cancelar → fusiona con los existentes");
                const fd = new FormData();
                fd.append("file", f);
                fd.append("mode", replace ? "replace" : "merge");
                try {
                  const { data } = await api.post("/admin/import-catalog", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                  });
                  toast.success(`Importados ${data.imported} productos. Total en catálogo: ${data.total_in_catalog}`);
                  window.location.reload();
                } catch (err) {
                  toast.error(formatApiError(err));
                }
                e.target.value = "";
              }}
            />
            <Button asChild variant="outline" data-testid="import-catalog-btn">
              <span className="cursor-pointer"><Upload className="h-4 w-4 mr-2" />Importar de proveedor</span>
            </Button>
          </label>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                setEditing(null);
                setForm({ brand: "", model: "", price: 0, notes: "", type: "audifono", warranty_years: 0, default_supplier_id: "", iva_category: "", pila_size: "" });
              }}
              data-testid="new-catalog-btn"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Plus className="h-4 w-4 mr-2" /> Añadir
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {editing != null ? "Editar producto" : "Nuevo producto"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tipo *</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="catalog-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATALOG_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.type === "pila" && (
                <div>
                  <Label>Tamaño pila</Label>
                  <Select value={form.pila_size} onValueChange={(v) => setForm({ ...form, pila_size: v })}>
                    <SelectTrigger data-testid="catalog-pila-size"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {["675","13","312","10"].map((s) => (
                        <SelectItem key={s} value={s}>Pila {s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Marca *</Label>
                <Input
                  data-testid="catalog-brand"
                  required
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                />
              </div>
              <div>
                <Label>Modelo *</Label>
                <Input
                  data-testid="catalog-model"
                  required
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                />
              </div>
              <div>
                <Label>Precio final de venta (IVA incl.) €*</Label>
                <Input
                  data-testid="catalog-price"
                  type="number"
                  step="0.01"
                  required
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
                <p className="text-[11px] text-[#6E7772] mt-1">
                  Es el PVP que paga el cliente. El desglose de IVA se calcula automáticamente hacia atrás en facturas y presupuestos.
                </p>
              </div>
              <div>
                <Label>Garantía (años)</Label>
                <Input
                  data-testid="catalog-warranty"
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.warranty_years}
                  onChange={(e) => setForm({ ...form, warranty_years: e.target.value })}
                  placeholder="Ej. 2"
                />
              </div>
              <div>
                <Label>Proveedor por defecto</Label>
                <Select
                  value={form.default_supplier_id || "none"}
                  onValueChange={(v) => setForm({ ...form, default_supplier_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="catalog-supplier"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin proveedor</SelectItem>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoría IVA</Label>
                <Select
                  value={form.iva_category || "none"}
                  onValueChange={(v) => setForm({ ...form, iva_category: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="catalog-iva"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Auto</SelectItem>
                    {ivaCategories.map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.label} ({c.iva_rate}%)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Notas / descripción</Label>
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
              <DialogFooter className="md:col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="save-catalog-btn"
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {filteredItems.length === 0 ? (
          <div className="text-center text-[#6E7772] py-8">
            {typeFilter === "all"
              ? "Catálogo vacío. Añade productos para usarlos como referencia al crear stock o facturar."
              : `Sin productos del tipo "${TYPE_LABEL_MAP[typeFilter]}". Cambia el filtro o añade uno nuevo.`}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[#E5E3DB]">
                <TableHead>Tipo</TableHead>
                <TableHead>Marca</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead className="text-right">Precio (IVA incl.)</TableHead>
                <TableHead className="text-center">Garantía</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((it, viewIdx) => (
                <TableRow key={viewIdx} className="border-[#E5E3DB]">
                  <TableCell>
                    <Badge variant="outline" className="border-[#E5E3DB] font-normal text-xs">
                      {TYPE_LABEL_MAP[it.type || "audifono"] || it.type}
                    </Badge>
                    {it.type === "pila" && it.pila_size && (
                      <Badge className="ml-1 bg-[#EAE6DF] text-[#2A2F2C] text-xs">{it.pila_size}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{it.brand}</TableCell>
                  <TableCell>{it.model}</TableCell>
                  <TableCell className="text-right">{euro(it.price)}</TableCell>
                  <TableCell className="text-center text-xs text-[#6E7772]">
                    {it.warranty_years ? `${it.warranty_years} años` : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-[#6E7772]">
                    {it.default_supplier_id ? supplierName(it.default_supplier_id) : "—"}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditing(filteredIndexes[viewIdx]);
                        setForm({
                          brand: it.brand,
                          model: it.model,
                          price: it.price,
                          notes: it.notes || "",
                          type: it.type || "audifono",
                          warranty_years: it.warranty_years || 0,
                          default_supplier_id: it.default_supplier_id || "",
                          iva_category: it.iva_category || "",
                          pila_size: it.pila_size || "",
                        });
                        setOpen(true);
                      }}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(viewIdx)}
                    >
                      <Trash2 className="h-4 w-4 text-[#C07F67]" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function DataTab() {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("merge");
  const [exportFmt, setExportFmt] = useState("json"); // json | excel | csv
  const [importFmt, setImportFmt] = useState("json"); // json | excel | csv

  const doExport = async () => {
    setBusy(true);
    try {
      if (exportFmt === "json") {
        const { data } = await api.get("/admin/export");
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `gestionaudio-export-${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (exportFmt === "excel") {
        const resp = await api.get("/admin/export/excel", { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `gestionaudio-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (exportFmt === "csv") {
        const resp = await api.get("/admin/export/csv", { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `gestionaudio-export-${new Date().toISOString().slice(0, 10)}-csv.zip`;
        a.click();
        URL.revokeObjectURL(url);
      }
      toast.success(`Exportación ${exportFmt.toUpperCase()} descargada`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doImport = async (file) => {
    if (!file) return;
    if (!window.confirm(
      mode === "replace"
        ? "⚠️ Esta acción REEMPLAZA todos los datos existentes. ¿Continuar?"
        : "Se fusionarán los datos del archivo con los actuales (upsert por id). ¿Continuar?"
    )) return;
    setBusy(true);
    try {
      let resp;
      const lower = file.name.toLowerCase();
      const fmt =
        importFmt !== "json"
          ? importFmt
          : lower.endsWith(".xlsx") || lower.endsWith(".xls")
            ? "excel"
            : lower.endsWith(".zip")
              ? "csv"
              : "json";
      if (fmt === "excel") {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("mode", mode);
        resp = await api.post("/admin/import/excel", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else if (fmt === "csv") {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("mode", mode);
        resp = await api.post("/admin/import/csv", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        const text = await file.text();
        const data = JSON.parse(text);
        resp = await api.post("/admin/import", { data, mode });
      }
      const total = Object.values(resp.data.imported || {}).reduce((a, b) => a + b, 0);
      toast.success(`Importadas ${total} entradas (${fmt.toUpperCase()})`);
    } catch (err) {
      toast.error(err?.message || formatApiError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4">
      <AsesoriaCard />
      <Card className="border-[#E5E3DB] shadow-none bg-white">
        <CardHeader>
          <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
            <Download className="h-4 w-4 text-[#4A5D4E]" strokeWidth={1.5} /> Exportar todos los datos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#6E7772] mb-4">
            Descarga una copia completa con todos los datos del sistema (pacientes, facturas,
            presupuestos, citas, stock, fichajes, …). Las contraseñas no se incluyen.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
            <div className="flex-1 max-w-[260px]">
              <Label>Formato</Label>
              <Select value={exportFmt} onValueChange={setExportFmt}>
                <SelectTrigger data-testid="export-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="json">JSON (completo, recomendado para backup)</SelectItem>
                  <SelectItem value="excel">Excel (.xlsx multi-hoja)</SelectItem>
                  <SelectItem value="csv">CSV (ZIP con un fichero por colección)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={doExport}
              disabled={busy}
              data-testid="export-data-btn"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Download className="h-4 w-4 mr-2" />
              {busy ? "Procesando…" : `Descargar ${exportFmt.toUpperCase()}`}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB] shadow-none bg-white">
        <CardHeader>
          <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
            <Database className="h-4 w-4 text-[#4A5D4E]" strokeWidth={1.5} /> Importar datos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-[#F3E5D8] bg-[#FDF6EF] text-[#7A5538] text-xs px-3 py-2 flex items-start gap-2 mb-4">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Acción crítica: realiza siempre una exportación previa por si necesitas revertir.
              El modo "Reemplazar" borra todo antes de insertar.
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-start">
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="w-48" data-testid="import-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="merge">Fusionar (upsert)</SelectItem>
                <SelectItem value="replace">Reemplazar todo</SelectItem>
              </SelectContent>
            </Select>
            <Select value={importFmt} onValueChange={setImportFmt}>
              <SelectTrigger className="w-48" data-testid="import-format-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="json">JSON</SelectItem>
                <SelectItem value="excel">Excel (.xlsx)</SelectItem>
                <SelectItem value="csv">CSV (ZIP)</SelectItem>
              </SelectContent>
            </Select>
            <input
              ref={fileRef}
              type="file"
              accept={
                importFmt === "json"
                  ? "application/json,.json"
                  : importFmt === "excel"
                    ? ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    : ".zip,application/zip"
              }
              className="hidden"
              data-testid="import-file"
              onChange={(e) => doImport(e.target.files?.[0])}
            />
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              data-testid="import-data-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {busy ? "Procesando…" : `Seleccionar ${importFmt.toUpperCase()}`}
            </Button>
          </div>
        </CardContent>
      </Card>

      <HistoricalSnapshotCard />

      <BackupSection />

      <PrinterSection />

      <ProdSyncSection />

      <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="desktop-app-card">
        <CardHeader>
          <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
            <Download className="h-4 w-4" strokeWidth={1.5} /> Aplicación de escritorio (Windows)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#6E7772] mb-4">
            Descarga la versión portable de GestiónAudio para Windows. Tras descomprimir el ZIP,
            ejecuta <code className="text-xs bg-[#F7F6F3] px-1 py-0.5 rounded">GestionAudio.exe</code>.
            La app se conecta automáticamente a tu servidor.
          </p>
          <a
            href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/desktop/download`}
            target="_blank"
            rel="noreferrer"
            data-testid="download-desktop-btn"
          >
            <Button className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white">
              <Download className="h-4 w-4 mr-2" />
              Descargar GestiónAudio para Windows (≈108 MB)
            </Button>
          </a>
        </CardContent>
      </Card>

      <Card className="border-[#E8D1D1] shadow-none bg-white">
        <CardHeader>
          <CardTitle className="font-heading font-medium text-lg flex items-center gap-2 text-[#C07F67]">
            <AlertTriangle className="h-4 w-4" strokeWidth={1.5} /> Empezar de cero
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#6E7772] mb-4">
            Borra todos los pacientes, anamnesis, audífonos, stock, proveedores, facturas, presupuestos,
            citas, chat, fichajes y notificaciones. Conserva los centros y tu cuenta de administrador.
            Esta acción <strong>no se puede deshacer</strong>; exporta primero los datos si quieres
            poder volver al estado actual.
          </p>
          <Button
            variant="outline"
            onClick={async () => {
              if (!window.confirm("¿Seguro? Se borrarán TODOS los datos (excepto centros y tu cuenta de administrador). Esta acción no se puede deshacer.")) {
                return;
              }
              setBusy(true);
              try {
                const { data } = await api.post("/admin/reset");
                const total = Object.values(data.deleted || {}).reduce((a, b) => a + Number(b || 0), 0);
                toast.success(`Reset completado: ${total} registros eliminados. Recargando…`);
                setTimeout(() => window.location.reload(), 1200);
              } catch (err) {
                toast.error(formatApiError(err));
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
            data-testid="reset-all-btn"
            className="border-[#C07F67] text-[#C07F67] hover:bg-[#FDF3EE]"
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            {busy ? "Procesando…" : "Borrar TODOS los datos"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}


function NoahTab() {
  const [centers, setCenters] = useState([]);
  const [allConfigs, setAllConfigs] = useState({});
  const [selectedCenter, setSelectedCenter] = useState("");
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadAll = async () => {
    const { data } = await api.get("/noah/configs");
    setCenters(data.centers || []);
    setAllConfigs(data.configs || {});
    if (!selectedCenter && (data.centers || []).length) {
      setSelectedCenter(data.centers[0].id);
      setCfg(data.configs[data.centers[0].id]);
    } else if (selectedCenter) {
      setCfg(data.configs[selectedCenter]);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, []);

  // Cambiar de centro → carga su config
  useEffect(() => {
    if (selectedCenter && allConfigs[selectedCenter]) {
      setCfg(allConfigs[selectedCenter]);
      setTestResult(null);
    }
  }, [selectedCenter, allConfigs]);

  if (!cfg) {
    return <div className="text-sm text-[#6E7772]">Cargando…</div>;
  }

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/noah/config`, cfg, { params: { center_id: selectedCenter } });
      const next = { ...allConfigs, [selectedCenter]: data };
      setAllConfigs(next);
      setCfg(data);
      toast.success("Configuración Noah guardada para este centro");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const testConn = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/noah/test-connection`, null, {
        params: { center_id: selectedCenter },
      });
      setTestResult(data);
      if (data.ok) {
        toast.success(`Conexión OK · ${data.patients_count ?? 0} pacientes en Noah ES`);
      } else {
        toast.error(`Fallo: ${data.error || "desconocido"}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  const runSync = async (direction) => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/noah/sync`, null, {
        params: { direction, center_id: selectedCenter },
      });
      const s = (data.summary && data.summary[selectedCenter]) || {};
      toast.success(
        `Sync OK · Pull ${s.pulled_new || 0} nuevos / ${s.pulled_updated || 0} act. · Push ${s.pushed_new || 0} nuevos`,
      );
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncing(false);
    }
  };

  const runSyncAll = async () => {
    setSyncingAll(true);
    try {
      const { data } = await api.post(`/noah/sync`, null, { params: { direction: "both" } });
      const total = Object.entries(data.summary || {}).reduce(
        (acc, [, s]) =>
          acc +
          (s.pulled_new || 0) +
          (s.pulled_updated || 0) +
          (s.pushed_new || 0),
        0,
      );
      const errs = Object.values(data.summary || {}).filter((s) => s.error || (s.errors && s.errors.length)).length;
      toast.success(`Sync TODOS los centros · ${total} cambios procesados${errs ? ` · ${errs} centros con errores` : ""}`);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncingAll(false);
    }
  };

  const deleteConfig = async () => {
    if (!window.confirm("¿Borrar la configuración Noah de este centro? El centro dejará de sincronizarse.")) return;
    try {
      await api.delete(`/noah/config`, { params: { center_id: selectedCenter } });
      toast.success("Configuración eliminada");
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const isConfigured = cfg.tenant_id && cfg.api_key;
  const currentCenterName = centers.find((c) => c.id === selectedCenter)?.name || "—";
  const activeCount = Object.values(allConfigs).filter((c) => c.enabled && c.tenant_id).length;

  return (
    <div className="space-y-6" data-testid="noah-tab">
      {/* Resumen global */}
      <Card className="border-[#E5E3DB] shadow-none bg-white">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            <span className="text-[#6E7772]">Centros con Noah ES activo:</span>{" "}
            <span className="font-medium text-[#2A2F2C]">{activeCount}</span>{" "}
            <span className="text-[#6E7772]">de</span>{" "}
            <span className="font-medium text-[#2A2F2C]">{centers.length}</span>
          </div>
          <Button
            variant="outline"
            onClick={runSyncAll}
            disabled={syncingAll || activeCount === 0}
            data-testid="noah-sync-all"
            className="border-[#4A5D4E] text-[#4A5D4E]"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${syncingAll ? "animate-spin" : ""}`} />
            {syncingAll ? "Sincronizando…" : "Sincronizar TODOS los centros"}
          </Button>
        </CardContent>
      </Card>

      {/* Selector de centro */}
      <Card className="border-[#E5E3DB] shadow-none bg-white">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Label className="m-0 text-sm whitespace-nowrap">Configurar centro:</Label>
          <div className="flex-1 min-w-[200px] max-w-md">
            <Select value={selectedCenter} onValueChange={setSelectedCenter}>
              <SelectTrigger data-testid="noah-center-selector" className="bg-white">
                <SelectValue placeholder="Selecciona centro…" />
              </SelectTrigger>
              <SelectContent>
                {centers.map((c) => {
                  const conf = allConfigs[c.id];
                  const dot = conf?.enabled && conf?.tenant_id
                    ? "●"
                    : "○";
                  return (
                    <SelectItem key={c.id} value={c.id}>
                      <span className={conf?.enabled && conf?.tenant_id ? "text-[#4A5D4E]" : "text-[#6E7772]"}>
                        {dot}
                      </span>{" "}
                      {c.name}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          {isConfigured && (
            <Button
              variant="ghost"
              size="sm"
              onClick={deleteConfig}
              className="text-[#C07F67] hover:bg-[#FDF3EE]"
              data-testid="noah-config-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Borrar config de este centro
            </Button>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB] shadow-none bg-white">
        <CardHeader>
          <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
            <Plug className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} />
            Noah ES — {currentCenterName}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-[#6E7772]">
            Cada centro puede conectarse a su propia cuenta Noah ES con tenant y API key
            independientes. Configura aquí las credenciales del centro <strong>{currentCenterName}</strong>.
          </p>

          <div className="rounded-lg bg-[#F7F6F3] border border-[#E5E3DB] p-4 text-xs text-[#4B5563] space-y-1.5">
            <div className="font-semibold text-[#2A2F2C] uppercase tracking-wider">
              Cómo obtener las credenciales
            </div>
            <ol className="list-decimal ml-4 space-y-0.5">
              <li>Como miembro HIMSA, abre un ticket en <a href="https://himsanoah.atlassian.net/servicedesk/customer/portal/1/group/1/create/54" target="_blank" rel="noreferrer" className="text-[#4A5D4E] underline">Service Desk → Application Registration</a>.</li>
              <li>Solicita un tenant POR CENTRO si quieres separación contable o uno único compartido si te lo permiten.</li>
              <li>Pega los valores aquí, marca «Activar» y prueba la conexión.</li>
              <li>Para producción (US/EU/AU) contacta con HIMSA y certifica anualmente.</li>
            </ol>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2 md:col-span-2">
              <Checkbox
                checked={!!cfg.enabled}
                onCheckedChange={(v) => setCfg({ ...cfg, enabled: !!v })}
                data-testid="noah-enabled"
              />
              <Label className="m-0">Activar Noah ES para este centro</Label>
            </div>
            <div className="md:col-span-2">
              <Label>URL base</Label>
              <Input
                value={cfg.base_url || ""}
                onChange={(e) => setCfg({ ...cfg, base_url: e.target.value })}
                placeholder="https://api.qa.eu.noah-es.com"
                data-testid="noah-base-url"
              />
              <p className="text-[11px] text-[#6E7772] mt-1">
                QA EU: <code>https://api.qa.eu.noah-es.com</code> · Producción: contactar con HIMSA.
              </p>
            </div>
            <div>
              <Label>Tenant ID (GUID)</Label>
              <Input
                value={cfg.tenant_id || ""}
                onChange={(e) => setCfg({ ...cfg, tenant_id: e.target.value })}
                placeholder="00000000-0000-0000-0000-000000000000"
                data-testid="noah-tenant-id"
              />
            </div>
            <div>
              <Label>Manufacturer code</Label>
              <Input
                value={cfg.manufacturer_code || ""}
                onChange={(e) => setCfg({ ...cfg, manufacturer_code: e.target.value })}
                placeholder="AUDIOCENTER"
                data-testid="noah-manufacturer-code"
              />
            </div>
            <div className="md:col-span-2">
              <Label>API Key</Label>
              <Input
                type="password"
                value={cfg.api_key || ""}
                onChange={(e) => setCfg({ ...cfg, api_key: e.target.value })}
                placeholder="Pega aquí la API key que HIMSA te ha entregado"
                data-testid="noah-api-key"
              />
              <p className="text-[11px] text-[#6E7772] mt-1">
                Por seguridad, una vez guardada solo se muestra «••••••••». Si no la editas,
                conservamos la actual.
              </p>
            </div>
            <div>
              <Label>API Version</Label>
              <Input
                value={cfg.api_version || ""}
                onChange={(e) => setCfg({ ...cfg, api_version: e.target.value })}
                placeholder="1.22"
                data-testid="noah-api-version"
              />
            </div>
          </div>

          <details className="text-xs">
            <summary className="cursor-pointer text-[#4A5D4E] font-medium">
              ⚙️ Opciones avanzadas (OAuth2 client_credentials)
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 pl-3 border-l-2 border-[#EAE6DF]">
              <div className="md:col-span-2">
                <Label>OAuth Token URL</Label>
                <Input
                  value={cfg.oauth_token_url || ""}
                  onChange={(e) => setCfg({ ...cfg, oauth_token_url: e.target.value })}
                  placeholder="https://identity.noah-es.com/oauth/token (opcional)"
                />
              </div>
              <div>
                <Label>Client ID</Label>
                <Input
                  value={cfg.client_id || ""}
                  onChange={(e) => setCfg({ ...cfg, client_id: e.target.value })}
                />
              </div>
              <div>
                <Label>Client Secret</Label>
                <Input
                  type="password"
                  value={cfg.client_secret || ""}
                  onChange={(e) => setCfg({ ...cfg, client_secret: e.target.value })}
                />
              </div>
            </div>
          </details>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={save}
              disabled={saving}
              data-testid="noah-save"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              {saving ? "Guardando…" : "Guardar este centro"}
            </Button>
            <Button
              variant="outline"
              onClick={testConn}
              disabled={testing || !isConfigured || !cfg.enabled}
              data-testid="noah-test"
            >
              <Plug className="h-4 w-4 mr-1" />
              {testing ? "Probando…" : "Probar conexión"}
            </Button>
            <Button
              variant="outline"
              onClick={() => runSync("pull")}
              disabled={syncing || !isConfigured || !cfg.enabled}
              data-testid="noah-sync-pull"
            >
              <Download className="h-4 w-4 mr-1" /> Traer de Noah
            </Button>
            <Button
              variant="outline"
              onClick={() => runSync("push")}
              disabled={syncing || !isConfigured || !cfg.enabled}
              data-testid="noah-sync-push"
            >
              <Upload className="h-4 w-4 mr-1" /> Enviar a Noah
            </Button>
            <Button
              variant="outline"
              onClick={() => runSync("both")}
              disabled={syncing || !isConfigured || !cfg.enabled}
              data-testid="noah-sync-both"
              className="border-[#4A5D4E] text-[#4A5D4E]"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizando…" : "Sincronizar este centro"}
            </Button>
          </div>

          {testResult && (
            <div
              className={`text-sm rounded-md px-3 py-2 border ${
                testResult.ok
                  ? "bg-[#D1E0D7]/40 border-[#A8C7B0] text-[#2A4A35]"
                  : "bg-[#E8D1D1]/40 border-[#C9A4A4] text-[#5C2A2A]"
              }`}
              data-testid="noah-test-result"
            >
              {testResult.ok ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Conexión OK · {testResult.patients_count ?? 0} pacientes en el tenant ·
                  Tenant <code>{testResult.tenant_id}</code>
                </div>
              ) : (
                <div>
                  <div className="font-medium">Fallo en la conexión ({testResult.status_code || "—"})</div>
                  <div className="text-xs mt-1 break-all">{testResult.error}</div>
                </div>
              )}
            </div>
          )}

          {cfg.last_sync_at && (
            <div className="text-xs text-[#6E7772] pt-2 border-t border-[#EAE6DF]">
              Última sincronización: {new Date(cfg.last_sync_at).toLocaleString("es-ES")}
              {cfg.last_sync_summary && (
                <span>
                  {" "}
                  · Pull: {cfg.last_sync_summary.pulled_new || 0} nuevos /{" "}
                  {cfg.last_sync_summary.pulled_updated || 0} actualizados · Push:{" "}
                  {cfg.last_sync_summary.pushed_new || 0} nuevos
                  {cfg.last_sync_summary.errors?.length > 0 &&
                    ` · ${cfg.last_sync_summary.errors.length} errores`}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function VerifactuTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const [certPassword, setCertPassword] = useState("");

  const load = async () => {
    const { data } = await api.get("/verifactu/config");
    setCfg(data);
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="text-sm text-[#6E7772]">Cargando…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/verifactu/config", cfg);
      setCfg(data);
      toast.success("Configuración Veri*Factu guardada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadCert = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) {
      toast.error("Selecciona tu fichero .p12 o .pfx");
      return;
    }
    if (!certPassword) {
      toast.error("Introduce la contraseña del certificado");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("password", certPassword);
      const { data } = await api.post("/verifactu/upload-certificate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Certificado cargado: ${data.cert?.subject?.slice(0, 60) || "OK"}`);
      setCertPassword("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const removeCert = async () => {
    if (!window.confirm("¿Eliminar el certificado del servidor? Tendrás que volver a subirlo para enviar facturas.")) return;
    try {
      await api.delete("/verifactu/certificate");
      toast.success("Certificado eliminado");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitPending = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/verifactu/submit-pending");
      toast.success(`Enviadas a AEAT: ${data.sent} · Errores: ${data.errors}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="verifactu-tab">
      <Card className="border-[#E5E3DB] shadow-none bg-white">
        <CardHeader>
          <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
            <FileCheck2 className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} />
            Veri*Factu — AEAT (Agencia Tributaria)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="rounded-lg bg-[#F7F6F3] border border-[#E5E3DB] p-4 text-xs text-[#4B5563] space-y-1.5">
            <div className="font-semibold text-[#2A2F2C] uppercase tracking-wider">
              Cómo funciona
            </div>
            <ul className="list-disc ml-4 space-y-0.5">
              <li>Cada factura genera automáticamente una <strong>huella SHA-256 encadenada</strong> con la anterior (inalterabilidad).</li>
              <li>Se imprime un <strong>código QR oficial AEAT</strong> en cada factura PDF (verificable en sede.aeat.es).</li>
              <li>Si activas el envío y subes tu certificado FNMT, las facturas se envían a AEAT (entorno preproducción por defecto).</li>
              <li>Obligatorio: <strong>1 julio 2027</strong> para autónomos (1 enero 2027 para sociedades). Ya quedas preparado.</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={!!cfg.enabled}
                onCheckedChange={(v) => setCfg({ ...cfg, enabled: !!v })}
                data-testid="vf-enabled"
              />
              <Label className="m-0">Activar Veri*Factu (hash + QR + envío)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={!!cfg.production}
                onCheckedChange={(v) => setCfg({ ...cfg, production: !!v })}
                data-testid="vf-production"
              />
              <Label className="m-0">Entorno PRODUCCIÓN (sino preproducción)</Label>
            </div>
            <div>
              <Label>NIF / DNI emisor *</Label>
              <Input
                value={cfg.nif_emisor || ""}
                onChange={(e) => setCfg({ ...cfg, nif_emisor: e.target.value.toUpperCase() })}
                placeholder="B12345678 ó 12345678Z"
                data-testid="vf-nif"
              />
            </div>
            <div>
              <Label>Razón social / Nombre fiscal *</Label>
              <Input
                value={cfg.razon_social || ""}
                onChange={(e) => setCfg({ ...cfg, razon_social: e.target.value })}
                placeholder="Tu nombre fiscal completo"
                data-testid="vf-razon"
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <Checkbox
                checked={!!cfg.auto_submit}
                onCheckedChange={(v) => setCfg({ ...cfg, auto_submit: !!v })}
                data-testid="vf-auto"
              />
              <Label className="m-0">Enviar a AEAT automáticamente al emitir cada factura</Label>
            </div>
          </div>

          <Button
            onClick={save}
            disabled={saving}
            data-testid="vf-save"
            className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
          >
            {saving ? "Guardando…" : "Guardar configuración"}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB] shadow-none bg-white">
        <CardHeader>
          <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} />
            Certificado digital FNMT
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {cfg.has_certificate ? (
            <div className="rounded-lg border border-[#A8C7B0] bg-[#D1E0D7]/40 p-3 text-sm space-y-1" data-testid="vf-cert-loaded">
              <div className="flex items-center gap-2 text-[#2A4A35]">
                <CheckCircle2 className="h-4 w-4" /> Certificado cargado correctamente
              </div>
              {cfg.cert_subject && (
                <div className="text-xs text-[#4B5563] break-all">
                  <strong>Titular:</strong> {cfg.cert_subject}
                </div>
              )}
              {cfg.cert_valid_to && (
                <div className="text-xs text-[#4B5563]">
                  <strong>Válido hasta:</strong> {new Date(cfg.cert_valid_to).toLocaleDateString("es-ES")}
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={removeCert}
                className="mt-2 border-[#C07F67] text-[#C07F67] hover:bg-[#FDF3EE]"
                data-testid="vf-cert-remove"
              >
                <Trash2 className="h-4 w-4 mr-1" /> Eliminar certificado
              </Button>
            </div>
          ) : (
            <div className="rounded-lg border border-[#E5E3DB] p-3 text-sm text-[#6E7772]">
              No hay certificado cargado. Necesitas un .p12 / .pfx con representante de la FNMT.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-[1fr_300px_auto] gap-2 items-end">
            <div>
              <Label>Fichero .p12 / .pfx</Label>
              <Input type="file" accept=".p12,.pfx" ref={fileRef} data-testid="vf-cert-file" />
            </div>
            <div>
              <Label>Contraseña del certificado</Label>
              <Input
                type="password"
                value={certPassword}
                onChange={(e) => setCertPassword(e.target.value)}
                data-testid="vf-cert-password"
              />
            </div>
            <Button
              onClick={uploadCert}
              disabled={uploading}
              data-testid="vf-cert-upload"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Upload className="h-4 w-4 mr-1" /> {uploading ? "Subiendo…" : "Subir"}
            </Button>
          </div>
          <p className="text-xs text-[#6E7772]">
            El fichero se guarda cifrado en el servidor y se usa sólo para firmar el XML que se envía a AEAT.
          </p>
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB] shadow-none bg-white">
        <CardHeader>
          <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
            <Send className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} />
            Envío a AEAT
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#6E7772] mb-3">
            Envía manualmente todas las facturas pendientes o con error. Si tienes activado "envío automático",
            las nuevas facturas se enviarán solas al emitirlas.
          </p>
          <Button
            onClick={submitPending}
            disabled={submitting || !cfg.enabled || !cfg.has_certificate}
            data-testid="vf-submit-pending"
            className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
          >
            <Send className={`h-4 w-4 mr-1 ${submitting ? "animate-pulse" : ""}`} />
            {submitting ? "Enviando…" : "Enviar facturas pendientes"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}


// --------------- Asesoría export (ZIP con facturas + gastos del periodo) ---------------
function AsesoriaCard() {
  const { user, centers } = useAuth();
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [centerId, setCenterId] = useState("all");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ date_from: from, date_to: to });
      if (centerId !== "all") params.append("center_id", centerId);
      // Stream blob
      const resp = await fetch(`${API_BASE}/admin/export/asesoria?${params.toString()}`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || ""}` },
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `asesoria-${from}-${to}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Asesoría exportada");
    } catch (err) {
      toast.error(err?.message || "No se pudo exportar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="asesoria-card">
      <CardHeader>
        <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
          <FileCheck2 className="h-4 w-4 text-[#4A5D4E]" strokeWidth={1.5} /> Asesoría — exportar trimestre
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-[#6E7772] mb-4">
          Descarga un ZIP con todas las facturas emitidas y las facturas de proveedores del periodo elegido,
          listas para enviar a tu asesoría o gestoría. Incluye además dos CSVs con el resumen.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label>Desde</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="asesoria-from" />
          </div>
          <div>
            <Label>Hasta</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="asesoria-to" />
          </div>
          <div>
            <Label>Centro</Label>
            <Select value={centerId} onValueChange={setCenterId}>
              <SelectTrigger data-testid="asesoria-center"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los centros</SelectItem>
                {centers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              onClick={submit}
              disabled={busy}
              data-testid="asesoria-export-btn"
              className="w-full bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Download className="h-4 w-4 mr-2" /> {busy ? "Generando…" : "Descargar ZIP"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


// ============================================================================
// Snapshot histórico 2026 — carga los datos importados desde Excel
// ============================================================================
function HistoricalSnapshotCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const loadHistorical = async () => {
    if (!window.confirm(
      "Se cargarán los datos históricos 2026 (pacientes, facturas, cuotas, trials, contactos, audífonos) fusionándolos con los existentes por id.\n\nEsta operación es idempotente — puedes pulsarla varias veces sin duplicar. ¿Continuar?"
    )) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/import-historical-2026");
      setResult(data.imported || {});
      const total = Object.values(data.imported || {}).reduce((a, b) => a + b, 0);
      toast.success(`Histórico 2026 cargado: ${total} registros fusionados`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-[#D4C7B4] shadow-none bg-[#FAF7F0]" data-testid="historical-snapshot-card">
      <CardHeader>
        <CardTitle className="font-heading font-medium text-lg flex items-center gap-2 text-[#4A5D4E]">
          <Database className="h-4 w-4" strokeWidth={1.5} /> Cargar histórico 2026
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-[#6E7772] mb-4">
          Carga los datos históricos que se importaron en 2026 desde los 5 Excel (ventas, cuotas,
          revisiones, trials): 200+ pacientes, 11 facturas, 2 financiaciones con cuotas, 17 audífonos
          en prueba, 10 audífonos vendidos y 180 contactos de seguimiento. La operación es idempotente
          (fusión por id) y puede ejecutarse varias veces sin duplicar.
        </p>
        <Button
          onClick={loadHistorical}
          disabled={busy}
          className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
          data-testid="load-historical-2026-btn"
        >
          <Upload className="h-4 w-4 mr-2" />
          {busy ? "Cargando…" : "Cargar histórico 2026"}
        </Button>
        {result && (
          <div className="mt-4 p-3 bg-white border border-[#E5E3DB] rounded text-xs" data-testid="historical-result">
            <div className="font-medium text-[#4A5D4E] mb-2">Registros fusionados por colección:</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {Object.entries(result).map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-[#6E7772]">{k}:</span>
                  <span className="font-medium">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}



// ============================================================================
// Copias de seguridad (admin global)
// ============================================================================
function PrinterSection() {
  const desktop = isDesktop();
  const [printers, setPrinters] = useState([]);
  const [selected, setSelected] = useState(getSavedPrinter() || "__none__");
  const [loading, setLoading] = useState(false);

  const loadPrinters = async () => {
    setLoading(true);
    try {
      const list = await listPrinters();
      setPrinters(list || []);
    } catch {
      setPrinters([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (desktop) loadPrinters();
    // eslint-disable-next-line
  }, []);

  const choose = (name) => {
    setSelected(name);
    savePrinter(name === "__none__" ? "" : name);
    toast.success(
      name === "__none__"
        ? "Impresión directa desactivada — los tickets se abrirán como PDF"
        : `Los tickets 80mm se imprimirán directamente en "${name}"`
    );
  };

  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="printer-card">
      <CardHeader>
        <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
          <Printer className="h-4 w-4 text-[#4A5D4E]" strokeWidth={1.5} />
          Impresora de tickets (80mm)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!desktop ? (
          <div className="rounded-md bg-[#F7F6F3] border border-[#E5E3DB] p-3 text-sm text-[#4B5563]" data-testid="printer-browser-note">
            La impresión directa solo está disponible en la <strong>app de escritorio GestiónAudio</strong> (v1.1 o superior, descargable más abajo).
            En el navegador los tickets se abren como PDF y se imprimen desde el visor.
          </div>
        ) : (
          <>
            <p className="text-xs text-[#6E7772]">
              Elige la impresora térmica donde se enviarán los tickets automáticamente (sin abrir el PDF). La selección se guarda en <strong>este equipo</strong>.
            </p>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs text-[#6E7772]">Impresora</Label>
                <Select value={selected} onValueChange={choose}>
                  <SelectTrigger className="bg-white mt-1" data-testid="printer-select">
                    <SelectValue placeholder="Elegir impresora…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— No imprimir directamente (abrir PDF) —</SelectItem>
                    {printers.map((p) => (
                      <SelectItem key={p.name} value={p.name}>
                        {p.name}{p.isDefault ? " (predeterminada)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={loadPrinters} disabled={loading} data-testid="printer-refresh">
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
            {printers.length === 0 && !loading && (
              <p className="text-xs text-[#7C5A2A]">No se detectaron impresoras. Comprueba que está encendida y conectada, y pulsa recargar.</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}


function BackupSection() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [emailCfg, setEmailCfg] = useState(null);
  const [recipientsText, setRecipientsText] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backups");
      setBackups(data || []);
    } catch {
      setBackups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/backups/email-config").then((r) => {
      setEmailCfg(r.data);
      setRecipientsText((r.data?.recipients || []).join(", "));
    }).catch(() => setEmailCfg(null));
  }, []);

  const saveEmailCfg = async () => {
    setSavingEmail(true);
    try {
      const recipients = recipientsText.split(",").map((s) => s.trim()).filter((s) => s.includes("@"));
      const { data } = await api.put("/backups/email-config", { enabled: emailCfg.enabled, recipients });
      setEmailCfg(data);
      setRecipientsText((data.recipients || []).join(", "));
      toast.success("Configuración de envío guardada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingEmail(false);
    }
  };

  const sendLatest = async () => {
    if (!backups.length) {
      toast.error("No hay ninguna copia todavía. Genera una primero.");
      return;
    }
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/backups/${backups[0].id}/send-email`);
      toast.success(data.attached ? "Copia enviada por email (ZIP adjunto)" : "Email enviado con enlace de descarga");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingEmail(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/backups/run");
      toast.success("Copia de seguridad generada");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  const download = async (bid) => {
    try {
      const resp = await api.get(`/backups/${bid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `gestionaudio-backup-${bid}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (bid) => {
    if (!window.confirm("¿Eliminar esta copia? No se puede deshacer.")) return;
    try {
      await api.delete(`/backups/${bid}`);
      toast.success("Copia eliminada");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fmtBytes = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="backups-card">
      <CardHeader>
        <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#4A5D4E]" strokeWidth={1.5} />
          Copias de seguridad
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md bg-[#F7F6F3] border border-[#E5E3DB] p-3 text-sm text-[#2A2F2C]">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-[#4A5D4E] mt-0.5" strokeWidth={1.5} />
            <div>
              <strong>Copia diaria automática</strong> a las <strong>03:00 (Europe/Madrid)</strong> con todos los datos y adjuntos.
              Se conservan las <strong>últimas 30 copias</strong>. Puedes <strong>descargar cualquier copia</strong> en formato ZIP para guardarla en tu PC o NAS.
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-[#6E7772]">
            ¿Necesitas una copia ahora mismo? Pulsa el botón y genérala bajo demanda.
          </p>
          <Button
            onClick={runNow}
            disabled={running}
            className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            data-testid="backup-run-now"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${running ? "animate-spin" : ""}`} />
            {running ? "Generando…" : "Generar copia ahora"}
          </Button>
        </div>

        {emailCfg && (
          <div className="rounded-md border border-[#E5E3DB] p-3 space-y-3" data-testid="backup-email-block">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm font-medium text-[#2A2F2C]">
                <Send className="h-4 w-4 text-[#4A5D4E]" strokeWidth={1.5} />
                Envío diario por email
              </div>
              <label className="flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer">
                <Checkbox
                  checked={!!emailCfg.enabled}
                  onCheckedChange={(v) => setEmailCfg({ ...emailCfg, enabled: !!v })}
                  data-testid="backup-email-enabled"
                />
                Activado
              </label>
            </div>
            {!emailCfg.resend_configured && (
              <div className="text-xs bg-[#FBF3E4] border border-[#E5D8C0] text-[#7C5A2A] rounded p-2" data-testid="backup-email-missing-key">
                Falta la clave de Resend en el servidor (RESEND_API_KEY). Crea una cuenta gratuita en resend.com con el email de destino y facilita la API key para activar el envío automático.
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs text-[#6E7772]">Destinatarios (separados por comas)</Label>
                <Input
                  value={recipientsText}
                  onChange={(e) => setRecipientsText(e.target.value)}
                  placeholder="tu@email.com"
                  className="mt-1"
                  data-testid="backup-email-recipients"
                />
              </div>
              <Button
                variant="outline"
                onClick={saveEmailCfg}
                disabled={savingEmail}
                data-testid="backup-email-save"
              >
                {savingEmail ? "Guardando…" : "Guardar"}
              </Button>
              <Button
                onClick={sendLatest}
                disabled={sendingEmail || !emailCfg.resend_configured || !backups.length}
                className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                data-testid="backup-email-send-now"
              >
                <Send className={`h-4 w-4 mr-2 ${sendingEmail ? "animate-pulse" : ""}`} />
                {sendingEmail ? "Enviando…" : "Enviar última copia"}
              </Button>
            </div>
            <p className="text-[11px] text-[#9CA3AF]">
              Cada mañana, tras la copia de las 03:00, se enviará el ZIP adjunto (si pesa menos de 35 MB) o un enlace de descarga seguro que caduca a los 7 días.
            </p>
          </div>
        )}

        <div className="border border-[#E5E3DB] rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-[#E5E3DB] bg-[#F7F6F3]">
                <TableHead>Fecha</TableHead>
                <TableHead className="text-right">Tamaño</TableHead>
                <TableHead className="text-right">Documentos</TableHead>
                <TableHead className="text-right">Adjuntos</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-[#6E7772] py-6 text-sm">Cargando…</TableCell>
                </TableRow>
              )}
              {!loading && backups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-[#6E7772] py-6 text-sm">
                    No hay copias todavía. La primera copia automática se generará esta noche, o pulsa &quot;Generar copia ahora&quot;.
                  </TableCell>
                </TableRow>
              )}
              {backups.map((b) => (
                <TableRow key={b.id} className="border-[#E5E3DB]" data-testid={`backup-row-${b.id}`}>
                  <TableCell className="font-mono text-xs">{fmtDate(b.generated_at)}</TableCell>
                  <TableCell className="text-right text-sm">{fmtBytes(b.size_bytes)}</TableCell>
                  <TableCell className="text-right text-sm">{b.total_docs ?? "—"}</TableCell>
                  <TableCell className="text-right text-sm">{b.attachments_count ?? "—"}</TableCell>
                  <TableCell className="text-xs">
                    {b.kind === "monthly" ? (
                      <Badge className="bg-[#E8E2F0] text-[#4A3D66] hover:bg-[#E8E2F0]" title="Snapshot mensual de solo lectura (se conservan 24 meses)">Mensual</Badge>
                    ) : b.triggered_by === "scheduler" ? (
                      <Badge className="bg-[#D1E0D7] text-[#2A2F2C]">Auto</Badge>
                    ) : (
                      <Badge variant="outline">Manual</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => download(b.id)}
                      data-testid={`backup-download-${b.id}`}
                    >
                      <Download className="h-3.5 w-3.5 mr-1" /> Descargar
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-[#9CA3AF] hover:text-[#C04848] ml-1"
                      onClick={() => remove(b.id)}
                      title="Eliminar copia"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}


// ============================================================================
// Sincronizar desde producción (trae los datos de la web publicada)
// ============================================================================
function ProdSyncSection() {
  const [form, setForm] = useState({ prod_url: "https://gestionaudio.com", employee_id: "", password: "" });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!form.employee_id || !form.password) {
      toast.error("Indica el usuario y la contraseña del admin de producción");
      return;
    }
    if (!window.confirm("Se traerán los datos de la web publicada a este entorno (se añaden y actualizan, no se borra nada). ¿Continuar?")) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/sync-from-production", form, { timeout: 300000 });
      setResult(data.synced || {});
      toast.success("Sincronización completada");
      setForm((f) => ({ ...f, password: "" }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="prod-sync-card">
      <CardHeader>
        <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-[#4A5D4E]" strokeWidth={1.5} />
          Sincronizar desde producción
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md bg-[#F7F6F3] border border-[#E5E3DB] p-3 text-sm text-[#2A2F2C]">
          Copia a este entorno los datos que tienes en la web publicada (pacientes, facturas, stock,
          catálogo, proveedores, citas…). <strong>No borra nada</strong>: añade y actualiza.
          Las credenciales no se guardan.
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label>URL de producción</Label>
            <Input
              value={form.prod_url}
              onChange={(e) => setForm({ ...form, prod_url: e.target.value })}
              data-testid="prod-sync-url"
            />
          </div>
          <div>
            <Label>ID de trabajador (admin de producción)</Label>
            <Input
              value={form.employee_id}
              onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
              data-testid="prod-sync-user"
            />
          </div>
          <div>
            <Label>Contraseña</Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="prod-sync-pass"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={run}
            disabled={running}
            className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            data-testid="prod-sync-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${running ? "animate-spin" : ""}`} />
            {running ? "Sincronizando… (puede tardar un minuto)" : "Sincronizar ahora"}
          </Button>
        </div>
        {result && (
          <div className="rounded-md border border-[#D1E0D7] bg-[#F2F7F3] p-3 text-sm" data-testid="prod-sync-result">
            <div className="font-medium text-[#2A2F2C] mb-1 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#4A5D4E]" /> Sincronizado correctamente
            </div>
            <div className="text-[#3F4744] flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {Object.entries(result)
                .filter(([k, v]) => typeof v === "number" && v > 0)
                .map(([k, v]) => (
                  <span key={k}><strong>{v}</strong> {k.replace(/_/g, " ")}</span>
                ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
